<script module lang="ts">
	import classNames from 'classnames';
	import { flip } from 'svelte/animate';
	import { writable } from 'svelte/store';
	import { fly } from 'svelte/transition';
	import { CrossIcon } from '../../assets/icons/CrossIcon';
	import { InfoIcon } from '../../assets/icons/InfoIcon';
	import { TickIcon } from '../../assets/icons/TickIcon';

	export type ToastVariants = 'success' | 'error' | 'info' | 'warning' | 'dark';

	type ToastData = {
		message: string;
		variant: ToastVariants;
	};

	type Toast = {
		id: string;
		data: ToastData;
		closeDelay: number;
	};

	let counter = 0;
	const defaultCloseDelay = 3000;

	const toasts = writable<Toast[]>([]);

	// Messages are Markdown, but the renderer (~72 KiB) is only fetched once a
	// toast is actually shown: <Toasts /> is mounted on many pages that never
	// show one. Until it arrives the message renders as plain text.
	let markdownModule: Promise<typeof import('../LazyMarkdown')> | undefined;
	const loadMarkdown = () => {
		if (!markdownModule) {
			markdownModule = import('../LazyMarkdown');
			// Not cached on failure, so the next toast retries the import.
			markdownModule.catch(() => {
				markdownModule = undefined;
			});
		}

		return markdownModule;
	};

	export const showToast = (
		{ message, variant }: ToastData,
		additionalOptions?: { closeDelay?: number }
	) => {
		const id = `toast-${counter++}`;
		void loadMarkdown();
		const closeDelay = additionalOptions?.closeDelay ?? defaultCloseDelay;

		toasts.update((t) => [...t, { id, data: { message, variant }, closeDelay }]);

		if (closeDelay > 0) {
			setTimeout(() => {
				closeToast(id);
			}, closeDelay);
		}

		return id;
	};

	export const closeToast = (id: string) => {
		toasts.update((t) => t.filter((toast) => toast.id !== id));
	};

	export const updateToast = (id: string, data: ToastData) => {
		toasts.update((t) => t.map((toast) => (toast.id === id ? { ...toast, data } : toast)));
	};

	export const clearToasts = () => {
		toasts.set([]);
	};
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import { whenPageIdle } from '../../utils/whenPageIdle/whenPageIdle';

	// Fetched once the page has loaded and gone idle: out of the scripts
	// preloaded for first paint, yet almost always ready before the first toast,
	// so its message rarely shows as plain text before rendering as Markdown.
	onMount(() =>
		whenPageIdle(() => {
			void loadMarkdown();
		})
	);
</script>

<div class="fixed bottom-0 right-0 z-50 m-4 flex flex-col items-end gap-2">
	{#each $toasts as { id, data: { message, variant } } (id)}
		<div
			role="alert"
			animate:flip={{ duration: 500 }}
			in:fly={{ duration: 150, x: '100%' }}
			out:fly={{ duration: 150, x: '100%' }}
			class={classNames('rounded-[4px] text-white shadow-md', {
				'bg-emerald-500': variant === 'success',
				'bg-red-500': variant === 'error',
				'bg-blue-500': variant === 'info',
				'bg-amber-500': variant === 'warning',
				'bg-gray-900': variant === 'dark'
			})}
		>
			<div
				class="relative flex w-[24rem] max-w-[calc(100vw-2rem)] items-center justify-between gap-4 p-4 pr-12"
			>
				<div class="flex items-center gap-4">
					{#if variant === 'success'}
						<TickIcon class="h-[22px] w-[22px] min-w-[22px]" color="gray-0" />
					{:else if variant === 'error'}
						<InfoIcon class="h-[22px] w-[22px] min-w-[22px]" color="gray-0" />
					{:else if variant === 'info'}
						<InfoIcon class="h-[22px] w-[22px] min-w-[22px]" color="gray-0" />
					{:else if variant === 'warning'}
						<InfoIcon class="h-[22px] w-[22px] min-w-[22px]" color="gray-0" />
					{:else}
						<InfoIcon class="h-[22px] w-[22px] min-w-[22px]" color="gray-0" />
					{/if}

					<div>
						{#await loadMarkdown()}
							<p class="font-medium text-white">{message}</p>
						{:then { LazyMarkdown }}
							<LazyMarkdown
								dataCy="toasts"
								size="medium"
								source={message}
								class="[&>p]:font-medium [&>p]:text-white [&>p]:mb-0 [&_a]:text-white"
							>
								{#snippet pending()}
									<p class="font-medium text-white">{message}</p>
								{/snippet}
							</LazyMarkdown>
						{:catch}
							<p class="font-medium text-white">{message}</p>
						{/await}
					</div>
				</div>

				<button
					onclick={() => closeToast(id)}
					class="absolute bottom-0 right-4 top-0 my-auto flex size-6 h-[20px] w-[20px] place-items-center
          hover:opacity-80"
				>
					<CrossIcon class="h-[16px] w-[16px]" color="gray-0" />
				</button>
			</div>
		</div>
	{/each}
</div>
